import { Maybe, MutationData, mt, rd, RemoteData } from '@passionware/monads';
import { useSimpleEventSubscription } from '@passionware/simple-event';
import { SimpleStore, useSimpleStore } from '@passionware/simple-store';
import { ArrowLeft } from 'lucide-react';
import { ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from 'v5/design-system/Button';
import {
  Dialog,
  DialogBody,
  DialogContentHeadless,
  DialogContentLayout,
  DialogDescription,
  DialogFooter,
  DialogTitle
} from 'v5/design-system/Dialog';
import { Form, FormRootElement } from '../../../design-system/Form';
import { AtellioFeatureGroupDefinition } from '../../../features';
import { DialogErrorFooter } from 'v5/patterns/forms/dialogs/DialogErrorFooter';
import { FormSaveButton } from 'v5/patterns/forms/dialogs/FormSaveButton';
import { FeatureFlagValues } from '../../../platform/feature-management/feature-management';
import { cn } from 'v5/platform/dom/cn';
import {
  FieldDefinition,
  FieldFormModel,
  formFieldModel
} from './field-definition';
import {
  CustomFieldAddSearchStage,
  type CustomFieldCatalogItem,
  type CustomFieldGroupCatalogItem
} from './CustomFieldAddSearchStage';
import { CustomFieldConfigFormContent } from './CustomFieldConfigFormContent';
import { getFields } from './fields';
import { useAssignedFieldsDraft } from './useAssignedFieldsDraft';

export type {
  CustomFieldCatalogItem,
  CustomFieldGroupCatalogItem
} from './CustomFieldAddSearchStage';

const EMPTY_ASSIGNED_ORDER: readonly number[] = [];

/**
 * Two-step flow: search / pick existing custom fields → configure a new field.
 * Intended to replace v1 `FieldAddModal` when wired to data and navigation.
 */
export interface CustomFieldAddDialogProps {
  features: Partial<FeatureFlagValues<AtellioFeatureGroupDefinition>>;
  resetStore: SimpleStore<RemoteData<Maybe<FieldDefinition>>>;
  onSubmit: (data: Maybe<FieldDefinition>) => void;
  onCancel: () => void;
  open: boolean;
  mutation: MutationData<unknown, unknown>;
  prompt: ReactNode;
  disabledFields?: (keyof FieldFormModel)[];
  hideDisabledFieldTypeOptions?: boolean;
  /** Existing fields shown in the search step. */
  catalog?: CustomFieldCatalogItem[];
  /** Field ids linked to target in initial render order. */
  initialAssignedOrder?: readonly number[];
  /** Field groups offered beside the fields (a structure's global fields only). */
  groupCatalog?: CustomFieldGroupCatalogItem[];
  /** Group ids already on the target. */
  initialAssignedGroupIds?: readonly number[];
  /** Commit selected existing-field (and group) assignments in one action. */
  onSaveFields?: (
    assignedIds: ReadonlySet<number>,
    assignedGroupIds: ReadonlySet<number>
  ) => void;
}

export function CustomFieldAddDialog(props: CustomFieldAddDialogProps) {
  const { t } = useTranslation('patterns');
  const initialField = useSimpleStore(props.resetStore);
  const form = useForm({
    defaultValues: rd.tryMap(initialField, formFieldModel.fromDefinition),
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const [stage, setStage] = useState<'search' | 'create'>('search');
  const [searchQuery, setSearchQuery] = useState('');

  useSimpleEventSubscription(props.resetStore.addUpdateListener, value =>
    rd.map(value, value => form.reset(formFieldModel.fromDefinition(value)))
  );

  const initialAssignedOrder =
    props.initialAssignedOrder ?? EMPTY_ASSIGNED_ORDER;
  const fieldsDraft = useAssignedFieldsDraft(props.open, initialAssignedOrder);
  const groupsDraft = useAssignedFieldsDraft(
    props.open,
    props.initialAssignedGroupIds ?? EMPTY_ASSIGNED_ORDER
  );

  useEffect(() => {
    if (props.open) {
      setStage('search');
      setSearchQuery('');
    }
  }, [props.open]);

  const handleSubmit = (data: FieldFormModel) => {
    props.onSubmit(formFieldModel.toDefinition(data));
  };

  const fields = getFields(props.features);

  const isFormValid = form.formState.isValid;
  const isFormDirty = form.formState.isDirty;
  const isSaveDisabled = !isFormValid || !isFormDirty;

  const catalog = props.catalog ?? [];
  const isAssignmentSubmitBlocked =
    !props.onSaveFields ||
    !(fieldsDraft.changed || groupsDraft.changed) ||
    mt.isInProgress(props.mutation);

  const goToCreateForm = (options?: { prefillName?: string }) => {
    setStage('create');
    const resetModel = {
      ...formFieldModel.fromDefinition(null),
      name: options?.prefillName ?? ''
    };

    form.reset(resetModel);
    /*
     * Prefill from search (e.g. "ab"): show name validation immediately (e.g. too short).
     * `reset()` does not surface that in the same turn; deferred `trigger('name')` does.
     */
    requestAnimationFrame(() => {
      void form.trigger('name');
    });
  };

  return (
    <Form {...form}>
      <Dialog
        open={props.open}
        onOpenChange={open => {
          if (!open) {
            props.onCancel();
          }
        }}
      >
        <DialogContentHeadless>
          <DialogContentLayout
            className={cn(
              'max-w-(--container-xl) w-full gap-0 overflow-hidden p-0',
              stage === 'search' ? `flex flex-col h-155` : ''
            )}
          >
            <DialogDescription className="sr-only">
              {t('CustomFieldAddFlow.stageDescription', {
                context: stage
              })}
            </DialogDescription>
            {stage === 'search' ? (
              <FormRootElement
                className="flex min-h-0 flex-1 flex-col"
                onSubmit={() => {
                  if (isAssignmentSubmitBlocked) return;
                  props.onSaveFields?.(fieldsDraft.draft, groupsDraft.draft);
                }}
              >
                <DialogTitle>{t('CustomFieldAddFlow.addTitle')}</DialogTitle>
                <CustomFieldAddSearchStage
                  searchQuery={searchQuery}
                  onSearchQueryChange={setSearchQuery}
                  catalog={catalog}
                  value={fieldsDraft.draft}
                  onChange={fieldsDraft.setDraft}
                  lockedAssignmentIds={fieldsDraft.initialAssignedIds}
                  groupCatalog={props.groupCatalog}
                  groupValue={groupsDraft.draft}
                  onGroupChange={groupsDraft.setDraft}
                  lockedGroupIds={groupsDraft.initialAssignedIds}
                  onOpenCreateFromSearchQuery={name =>
                    goToCreateForm({ prefillName: name })
                  }
                />
                <DialogFooter className="mt-auto gap-2 border-t border-border-default px-5 py-3 sm:flex-row sm:justify-end">
                  <FormSaveButton
                    disabled={isAssignmentSubmitBlocked}
                    mutation={props.mutation}
                    saveText={t('CustomFieldAddFlow.saveFields')}
                  />
                </DialogFooter>
              </FormRootElement>
            ) : (
              <FormRootElement onSubmit={form.handleSubmit(handleSubmit)}>
                <DialogTitle className="relative pl-14">
                  <Button
                    type="button"
                    variant="secondary"
                    layout="icon"
                    size="sm"
                    className="absolute left-1 top-1.5 m-2 size-9 shrink-0 p-0"
                    onClick={() => setStage('search')}
                    aria-label={t('CustomFieldAddFlow.back')}
                  >
                    <ArrowLeft className="size-4" />
                  </Button>
                  {t('CustomFieldAddFlow.createTitle')}
                </DialogTitle>
                <DialogBody className="space-y-6">
                  <CustomFieldConfigFormContent
                    initialField={initialField}
                    form={form}
                    fields={fields}
                    features={props.features}
                    disabledFields={props.disabledFields}
                    hideDisabledFieldTypeOptions={
                      props.hideDisabledFieldTypeOptions
                    }
                    prompt={props.prompt}
                    autoFocusName
                  />
                </DialogBody>
                <DialogFooter className="mt-auto gap-2 border-t border-border-default px-5 py-3 sm:flex-row sm:justify-end">
                  <DialogErrorFooter mutation={props.mutation} />
                  <FormSaveButton
                    disabled={isSaveDisabled}
                    mutation={props.mutation}
                    saveText={t('CustomFieldAddFlow.createAndAssign')}
                  />
                </DialogFooter>
              </FormRootElement>
            )}
          </DialogContentLayout>
        </DialogContentHeadless>
      </Dialog>
    </Form>
  );
}
